import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DialogService} from './dialog.service';
import {
  DynamicConfirmationDialogComponent
} from '../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';

const LOADING_TAB_STYLES = `
  html, body { height: 100%; margin: 0; }
  body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    font-family: Roboto, Arial, sans-serif;
    color: #616161;
    background: #fafafa;
  }
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e0e0e0;
    border-top-color: #3f51b5;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  #redirectHint {
    font-size: 0.85em;
    color: #9e9e9e;
    margin: -16px 0 0;
    white-space: pre-line;
    text-align: center;
  }
`;

function buildLoadingTabDocument(tab: Window): void {
  const style = tab.document.createElement('style');
  style.textContent = LOADING_TAB_STYLES;
  tab.document.head.appendChild(style);

  const spinner = tab.document.createElement('div');
  spinner.className = 'spinner';

  const message = tab.document.createElement('p');
  message.id = 'loadingMessage';

  const hint = tab.document.createElement('p');
  hint.id = 'redirectHint';

  tab.document.body.append(spinner, message, hint);
}

export interface PopupSafeTabLoadingTexts {
  messageKey: string;
  hintKey?: string;
}

export interface PopupBlockedDialogTexts {
  titleKey: string;
  messageKey: string;
  confirmKey: string;
  cancelKey: string;
}

export class PopupSafeTabHandle {
  constructor(
    private readonly tab: Window | null,
    private readonly dialogService: DialogService,
    private readonly returnUrl: string
  ) {
  }

  redirectTo(url: string, blockedTexts: PopupBlockedDialogTexts): void {
    if (this.tab && !this.tab.closed) {
      try {
        this.insertReturnStep();
        this.tab.location.href = url;
        this.tab.focus();
        return;
      } catch {
        // fall through to fallback dialog
      }
    }
    this.offerManualLink(url, blockedTexts);
  }

  private insertReturnStep(): void {
    try {
      // Adds a history entry for the origin page before navigating away, so
      // clicking the browser's back button in this tab returns to it instead
      // of the loading screen.
      this.tab?.history.pushState(null, '', this.returnUrl);
    } catch {
      // Best effort only; browsers may restrict this for the tab's initial
      // document. The redirect below still proceeds either way.
    }
  }

  close(): void {
    if (this.tab && !this.tab.closed) {
      this.tab.close();
    }
  }

  private offerManualLink(url: string, texts: PopupBlockedDialogTexts): void {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: texts.titleKey,
        message: texts.messageKey,
        confirmButtonText: texts.confirmKey,
        cancelButtonText: texts.cancelKey
      }
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        // Fired synchronously from the dialog's own confirm-button click,
        // so this is a trusted gesture and won't be popup-blocked.
        window.open(url, '_blank');
      }
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class PopupSafeTabService {

  constructor(
    private readonly dialogService: DialogService,
    private readonly translateService: TranslateService
  ) {
  }

  open(texts: PopupSafeTabLoadingTexts): PopupSafeTabHandle {
    // Open synchronously, inside the caller's click gesture call stack, so
    // the eventual redirect isn't treated as an untrusted popup.
    const tab = window.open('', '_blank');
    if (tab) {
      buildLoadingTabDocument(tab);
      const message = this.translateService.instant(texts.messageKey);
      tab.document.title = message;
      this.setElementText(tab, 'loadingMessage', message);
      if (texts.hintKey) {
        this.setElementText(tab, 'redirectHint', this.translateService.instant(texts.hintKey));
      }
    }
    return new PopupSafeTabHandle(tab, this.dialogService, globalThis.location.href);
  }

  private setElementText(tab: Window, elementId: string, text: string): void {
    const element = tab.document.getElementById(elementId);
    if (element) {
      element.textContent = text;
    }
  }
}
